"use client"

import { useMemo, useState } from "react"

import { FRAMEWORK_BY_ID, FRAMEWORKS } from "@/modules/lernpfad/data/frameworks"
import type { FrameworkFamily, FrameworkId } from "@/modules/lernpfad/data/frameworks"
import type { LernpfadDemoState } from "@/app/demo/lernpfad/use-lernpfad-demo"
import { overridesByCluster } from "./cluster-map"
import FrameworkDossier from "./framework-dossier"
import FrameworkRow from "./framework-row"

/**
 * The framework picker: a chooser ledger, and one dossier beside it.
 *
 * The previous version rendered six cards, each repeating the claim, the four or
 * five parameter effects and a source link, each ending in an equally-weighted
 * solid-blue "Auswählen". That is ~3,000 px of 11 px German prose and six
 * competing primary actions to make ONE choice — length scaled with the number
 * of options, because content that is read once per decision was duplicated once
 * per option.
 *
 * Splitting it by what is comparable fixes that. Name, the metric it moves, how
 * much policy it rewrites and the shape of its emphasis differ per framework and
 * belong on a row; the argument and its effects are read once and belong in a
 * single pane. Six rows fit above the fold, so a click and the resulting move in
 * the impact band are visible at the same time — which is the whole demo.
 *
 * The families are separated on screen for the same reason they are separated in
 * the data: the three ladders are competing answers to one question, so exactly
 * one can be held; everything else stacks. Radio vs. checkbox glyphs carry that
 * rule without a sentence.
 */

const FAMILY_ORDER: FrameworkFamily[] = ["ladder", "overlay", "design"]

export default function FrameworkGallery({ state }: { state: LernpfadDemoState }) {
    const { t, localizer, frameworks, toggleFramework, lastSwap, openCluster } = state

    // Hover/focus previews the dossier without committing. Cleared on leaving the
    // LIST, not the row — clearing per row flickers as the pointer crosses a gap.
    const [peek, setPeek] = useState<FrameworkId | null>(null)

    const writes = useMemo(() => {
        const byId = new Map<FrameworkId, number>()
        for (const framework of FRAMEWORKS) {
            const touched = overridesByCluster(framework.patch)
            byId.set(
                framework.id,
                Object.values(touched).reduce((sum, n) => sum + n, 0),
            )
        }
        return byId
    }, [])
    const writesScale = Math.max(...writes.values())

    // The dossier follows the pointer, falls back to what is selected, and
    // finally to the first framework — never to nothing. An empty pane beside a
    // full ledger reads as a loading failure, and the landing state is exactly
    // the one every cold visitor sees first.
    const subjectId = peek ?? frameworks[frameworks.length - 1] ?? FRAMEWORKS[0].id
    const subject = FRAMEWORK_BY_ID.get(subjectId)

    return (
        <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,24rem)_minmax(0,1fr)] xl:grid-cols-[26rem_minmax(0,1fr)] 2xl:grid-cols-[28rem_minmax(0,1fr)]">
            <div className="space-y-5" onPointerLeave={() => setPeek(null)}>
                {FAMILY_ORDER.map((family) => {
                    const members = FRAMEWORKS.filter((framework) => framework.family === family)
                    const exclusive = family === "ladder"
                    return (
                        <section
                            key={family}
                            role={exclusive ? "radiogroup" : "group"}
                            aria-labelledby={`framework-family-${family}`}
                        >
                            <div className="mb-2 flex flex-wrap items-baseline gap-x-2 gap-y-1">
                                <h3 id={`framework-family-${family}`} className="text-sm font-semibold">
                                    {t(`steuer.family.${family}`)}
                                </h3>
                                <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                                    {t(exclusive ? "steuer.rule.one" : "steuer.rule.any")}
                                </span>
                            </div>
                            {exclusive && (
                                <p className="mb-2 text-xs text-muted-foreground">{t("steuer.family.ladderHint")}</p>
                            )}
                            <div className="space-y-2">
                                {members.map((framework) => (
                                    <FrameworkRow
                                        key={framework.id}
                                        framework={framework}
                                        localizer={localizer}
                                        active={frameworks.includes(framework.id)}
                                        peeked={peek === framework.id}
                                        replacedBy={
                                            lastSwap?.from === framework.id
                                                ? localizer.framework(FRAMEWORK_BY_ID.get(lastSwap.to)!).name
                                                : undefined
                                        }
                                        writes={writes.get(framework.id) ?? 0}
                                        writesScale={writesScale}
                                        onSelect={() => toggleFramework(framework.id)}
                                        onPeek={() => setPeek(framework.id)}
                                        t={t}
                                    />
                                ))}
                            </div>
                        </section>
                    )
                })}
            </div>

            <FrameworkDossier
                subject={subject}
                localizer={localizer}
                selected={frameworks}
                frameworks={FRAMEWORKS}
                previewOnly={!frameworks.includes(subjectId)}
                onOpenCluster={openCluster}
                t={t}
            />
        </div>
    )
}
